import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { getAvatarUrl } from '../utils/imageUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Header = ({ currentPage, setCurrentPage }) => {
  const { user, logout } = useAuth();
  const [showMoreDropdown, setShowMoreDropdown] = useState(false);

  // Navigation items based on user membership tier
  const getMainNavItems = () => {
    const baseItems = [
      { id: 'feed', label: 'Feed', icon: '🏠' }
    ];

    // Starter, achiever, creator get direct access to 21-Day Reset
    if (['starter', 'achiever', 'creator'].includes(user?.membership_tier)) {
      return [
        ...baseItems,
        { id: '21-day-reset', label: '21-Day Reset', icon: '🎯' },
        { id: 'groups', label: 'Groups', icon: '👥' },
        { id: 'forum', label: 'Forum', icon: '💬' },
        { id: 'evolve90', label: 'Evolve 90 Dash', icon: '☀️' },
        { id: 'lms', label: 'Courses', icon: '🎓' },
        { id: 'news', label: 'News', icon: '📰' },
        { id: 'social', label: 'Social', icon: '💬' }
      ];
    }

    // Standard navigation for other tiers (builder)
    return [
      ...baseItems,
      { id: 'groups', label: 'Groups', icon: '👥' },
      { id: 'forum', label: 'Forum', icon: '💬' },
      { id: 'evolve90', label: 'Evolve 90 Dash', icon: '☀️' },
      { id: 'lms', label: 'Courses', icon: '🎓' },
      { id: 'news', label: 'News', icon: '📰' },
      { id: 'social', label: 'Social', icon: '💬' }
    ];
  };

  const mainNavItems = getMainNavItems();

  const getMoreNavItems = () => {
    const baseMoreItems = [
      { id: 'profile', label: 'Profile', icon: '👤' }
    ];

    // Standard more items for all tiers
    return [
      { id: 'landing', label: 'Landing', icon: '🚀' },
      { id: 'membership', label: 'Membership', icon: '⭐' },
      ...baseMoreItems
    ];
  };

  const moreNavItems = getMoreNavItems();

  if (user?.is_admin) {
    moreNavItems.push({ id: 'admin', label: 'Admin', icon: '⚙️' });
  }

  const getUserInitials = (name) => {
    return name.split(' ').map(word => word[0]).join('').toUpperCase();
  };

  return (
    <header className="app-header">
      <div className="header-content">
        <div className="logo-nav-section">
          <div className="logo-section">
            <img 
              src="/logo.webp" 
              alt="Zero Give Up" 
              className="logo-mini"
            />
            <span className="logo-text">ZeroGiveUp</span>
          </div>
          
          <nav>
            <ul className="nav-menu">
              {mainNavItems.map(item => (
                <li 
                  key={item.id}
                  className={`nav-item ${currentPage === item.id ? 'active' : ''}`}
                  onClick={() => setCurrentPage(item.id)}
                >
                  <span style={{ marginRight: '0.4rem' }}>{item.icon}</span>
                  {item.label}
                </li>
              ))}
              
              {/* More Dropdown */}
              <li 
                className={`nav-item dropdown ${moreNavItems.some(item => item.id === currentPage) ? 'active' : ''}`}
                onClick={() => setShowMoreDropdown(!showMoreDropdown)}
              >
                <span style={{ marginRight: '0.4rem' }}>⋯</span>
                More
                <span style={{ marginLeft: '0.4rem', fontSize: '0.8rem' }}>{showMoreDropdown ? '▲' : '▼'}</span>
                
                {showMoreDropdown && (
                  <ul className="dropdown-menu">
                    {moreNavItems.map(item => (
                      <li 
                        key={item.id}
                        className={`dropdown-item ${currentPage === item.id ? 'active' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setCurrentPage(item.id);
                          setShowMoreDropdown(false);
                        }}
                      >
                        <span style={{ marginRight: '0.4rem' }}>{item.icon}</span>
                        {item.label}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            </ul>
          </nav>
        </div>

        <div className="user-section">
          <NotificationBell setCurrentPage={setCurrentPage} />
          <div className="user-avatar">
            {user?.profile_photo ? (
              <img src={getAvatarUrl(user)} alt={user.name} />
            ) : (
              getUserInitials(user?.name || 'U')
            )}
          </div>
          <button className="logout-btn" onClick={logout}>
            Logout
          </button>
        </div>
      </div>
    </header>
  );
};

const NotificationBell = ({ setCurrentPage }) => {
  const [notificationCount, setNotificationCount] = useState(0);
  const [notificationDetails, setNotificationDetails] = useState({});
  const { user } = useAuth();

  const fetchNotificationCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/notifications/count`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setNotificationCount(data.total || 0);
        setNotificationDetails(data);
      }
    } catch (error) {
      console.error('Error fetching notification count:', error);
    }
  };

  const handleBellClick = () => {
    // Navigate to Social page and set appropriate tab based on notification type
    setCurrentPage('social');
    
    // Store notification context for Social component to use
    sessionStorage.setItem('socialTab', getPreferredTab());
  };

  const getPreferredTab = () => {
    if (notificationDetails.friend_requests > 0) {
      return 'friends'; // Will show friend requests
    } else if (notificationDetails.unread_messages > 0) {
      return 'messages'; // Will show messages
    }
    return 'friends'; // Default to friends tab
  };

  useEffect(() => {
    if (user) {
      fetchNotificationCount();
      // Poll every 30 seconds for real-time updates
      const interval = setInterval(fetchNotificationCount, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  return (
    <div className="notification-bell" onClick={handleBellClick}>
      <span className="bell-icon">🔔</span>
      {notificationCount > 0 && (
        <span className="notification-count">{notificationCount}</span>
      )}
    </div>
  );
};

export default Header;